import React, { useState } from 'react';
import { Row, Col, Card, Table, Badge, Button, Spinner, Alert, ProgressBar, Accordion } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { websiteService } from '../api/services/websites';
import { useToastStore } from "../components/UI/Toast";

// Registrace potřebných Chart.js komponent
Chart.register(...registerables);

const WebsiteSEO = ({ website, results }) => {
    const [loading, setLoading] = useState(false);
    const [scanInProgress, setScanInProgress] = useState(false);
    const [selectedHistoryPeriod, setSelectedHistoryPeriod] = useState('30days');
    const addToast = useToastStore(state => state.addToast);

    // Zkontrolujeme, zda máme validní data pro zobrazení
    const hasValidData = results && Array.isArray(results) && results.length > 0 &&
        results[0].details;

    // Funkce pro spuštění SEO kontroly
    const handleRunSeoScan = async (websiteId) => {
        setScanInProgress(true);
        try {
            await websiteService.runSeoCheck(websiteId);
            addToast('SEO kontrola byla úspěšně spuštěna. Výsledky budou brzy k dispozici.', 'success');
        } catch (error) {
            addToast('Nepodařilo se spustit SEO kontrolu.', 'danger');
        } finally {
            setScanInProgress(false);
        }
    };

    // Pokud nejsou k dispozici data
    if (!hasValidData) {
        return (
            <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                Pro tento web zatím nejsou k dispozici SEO data. Spusťte SEO kontrolu.
                <div className="mt-3">
                    <Button
                        variant="primary"
                        disabled={scanInProgress}
                        onClick={() => handleRunSeoScan(website.id)}
                    >
                        {scanInProgress ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Probíhá kontrola...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-search me-2"></i> Spustit SEO kontrolu
                            </>
                        )}
                    </Button>
                </div>
            </Alert>
        );
    }

    // Bezpečně získat data z výsledků
    const seoData = results[0].details;


    // Převést meta tagy na formát očekávaný komponentou
    const formatMetaTags = () => {
        const metaTags = [];

        if (seoData.meta_tags) {
            if (seoData.meta_tags.title) {
                const titleIssue = seoData.issues?.find(i => i.type === 'title_length');
                metaTags.push({
                    name: 'title',
                    content: seoData.meta_tags.title,
                    status: titleIssue ? 'improve' : 'ok',
                    recommendation: titleIssue ? titleIssue.message : ''
                });
            }

            if (seoData.meta_tags.description) {
                const descIssue = seoData.issues?.find(i => i.type === 'description_length');
                metaTags.push({
                    name: 'description',
                    content: seoData.meta_tags.description,
                    status: descIssue ? 'improve' : 'ok',
                    recommendation: descIssue ? descIssue.message : ''
                });
            }

            if (seoData.meta_tags.robots) {
                metaTags.push({
                    name: 'robots',
                    content: seoData.meta_tags.robots,
                    status: 'ok',
                    recommendation: ''
                });
            }
        }

        if (seoData.has_h1 !== undefined) {
            metaTags.push({
                name: 'h1',
                content: seoData.has_h1 ? 'Nalezen H1 tag' : 'Chybí H1 tag',
                status: seoData.has_h1 ? 'ok' : 'improve',
                recommendation: seoData.has_h1 ? '' : 'Přidejte hlavní nadpis H1 na stránku'
            });
        }

        if (seoData.canonical_url) {
            metaTags.push({
                name: 'canonical',
                content: seoData.canonical_url,
                status: 'ok',
                recommendation: ''
            });
        }

        return metaTags;
    };

    const metaTags = formatMetaTags();

    // Funkce pro výpočet SEO skóre
    const calculateSeoScore = () => {
        if (!seoData.issues || !Array.isArray(seoData.issues)) return 80;

        let score = 100;
        seoData.issues.forEach(issue => {
            if (issue.severity === 'high') {
                score -= 15;
            } else if (issue.severity === 'medium') {
                score -= 10;
            } else if (issue.severity === 'low') {
                score -= 5;
            }
        });

        return Math.max(0, score);
    };

    const seoScore = calculateSeoScore();

    // Příprava historických dat SEO skóre
    const prepareHistoricalSeoData = (results, period) => {
        // Určení limitního počtu výsledků podle zvoleného období
        const periodLimits = {
            '7days': 7,
            '30days': 30,
            '90days': 90
        };

        // Seřazení výsledků od nejnovějších
        const sortedResults = [...results].sort((a, b) =>
            new Date(b.createdAt) - new Date(a.createdAt)
        );

        // Omezení počtu výsledků
        const limitedResults = sortedResults.slice(0, periodLimits[period] || 30);

        // Příprava dat pro graf
        const labels = limitedResults.map(result =>
            new Date(result.createdAt).toLocaleDateString()
        ).reverse();

        const scores = limitedResults.map(result => {
            // Výpočet skóre pro každý historický výsledek
            const historicalScore = result.details.issues ?
                calculateSeoScore(result.details.issues) :
                seoScore;
            return historicalScore;
        }).reverse();

        return { labels, scores };
    };

    // Příprava dat pro graf
    const { labels, scores } = prepareHistoricalSeoData(results, selectedHistoryPeriod);

    // Data pro historický graf skóre
    const seoScoreHistory = {
        labels,
        datasets: [
            {
                label: 'SEO Skóre',
                data: scores,
                fill: false,
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                borderColor: 'rgba(54, 162, 235, 1)',
                tension: 0.1
            }
        ]
    };

    // Konfigurace grafu
    const historyChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
        },
        scales: {
            y: {
                beginAtZero: false,
                min: Math.max(0, Math.min(...scores) - 10),
                max: 100
            }
        }
    };

    // Určení barvy skóre
    const getScoreClass = (score) => {
        if (score >= 80) return 'success';
        if (score >= 60) return 'warning';
        return 'danger';
    };

    // Severita problémů
    const getSeverityBadge = (severity) => {
        const variants = {
            high: 'danger',
            medium: 'warning',
            low: 'info'
        };
        return <Badge bg={variants[severity] || 'secondary'}>{severity}</Badge>;
    };

    return (
        <div className="website-seo fade-in">
            <Row className="mb-4">
                <Col md={6} lg={4} className="mb-4">
                    <Card className="h-100">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">SEO Skóre</h5>
                            <Badge
                                bg={getScoreClass(seoScore)}
                                style={{ fontSize: '1.1rem', padding: '0.5rem 0.8rem' }}
                            >
                                {seoScore}/100
                            </Badge>
                        </Card.Header>
                        <Card.Body>
                            <ProgressBar
                                now={seoScore}
                                variant={getScoreClass(seoScore)}
                                className="my-3"
                                style={{ height: '1.5rem' }}
                            />
                            <div className="text-muted small">
                                <div className="mb-2">
                                    <i className="fas fa-calendar-alt me-2"></i>
                                    Poslední kontrola: {seoData.checked_at || 'Neznámé datum'}
                                </div>
                                <div>
                                    <i className="fas fa-info-circle me-2"></i>
                                    {seoData.issues ? `Nalezeno ${seoData.issues.length} SEO problémů` : 'Nebyly nalezeny žádné SEO problémy'}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} lg={8} className="mb-4">
                    <Card className="h-100">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Historie SEO skóre</h5>
                            <div className="btn-group">
                                <Button
                                    variant={selectedHistoryPeriod === '7days' ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    onClick={() => setSelectedHistoryPeriod('7days')}
                                >
                                    7 dní
                                </Button>
                                <Button
                                    variant={selectedHistoryPeriod === '30days' ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    onClick={() => setSelectedHistoryPeriod('30days')}
                                >
                                    30 dní
                                </Button>
                                <Button
                                    variant={selectedHistoryPeriod === '90days' ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    onClick={() => setSelectedHistoryPeriod('90days')}
                                >
                                    90 dní
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div style={{ height: '250px' }}>
                                <Line
                                    data={seoScoreHistory}
                                    options={{
                                        maintainAspectRatio: false,
                                        scales: {
                                            y: {
                                                beginAtZero: false,
                                                min: Math.max(0, Math.min(...scores) - 10),
                                                max: 100
                                            }
                                        }
                                    }}
                                />
                            </div>
                            <div className="text-center mt-2">
                                <small className="text-muted">Graf zobrazuje historii SEO skóre</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>


            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Meta tagy a hlavičky</h5>
                        </Card.Header>
                        <Card.Body>
                            {metaTags.length > 0 ? (
                                <Table responsive className="mb-0">
                                    <thead>
                                    <tr>
                                        <th>Element</th>
                                        <th>Obsah</th>
                                        <th>Status</th>
                                        <th>Doporučení</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {metaTags.map((tag, index) => (
                                        <tr key={index}>
                                            <td><strong>{tag.name}</strong></td>
                                            <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {tag.content}
                                            </td>
                                            <td>
                                                <Badge
                                                    bg={tag.status === 'ok' ? 'success' : 'warning'}
                                                    className="px-2 py-1"
                                                >
                                                    <i className={`fas fa-${tag.status === 'ok' ? 'check' : 'exclamation-triangle'} me-1`}></i>
                                                    {tag.status === 'ok' ? 'OK' : 'Vylepšit'}
                                                </Badge>
                                            </td>
                                            <td className="text-muted small">{tag.recommendation}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center py-4">
                                    <p>Žádné informace o meta tazích nejsou k dispozici.</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={6} className="mb-4">
                    <Card className="h-100">
                        <Card.Header>
                            <h5 className="mb-0">Rozbitá odkazy (404)</h5>
                        </Card.Header>
                        <Card.Body>
                            {seoData.broken_links > 0 ? (
                                <Alert variant="warning">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    Na webu bylo nalezeno {seoData.broken_links} rozbitých odkazů.
                                </Alert>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="fas fa-check-circle text-success" style={{ fontSize: '2rem' }}></i>
                                    <p className="mt-2">Nebyly nalezeny žádné rozbitá odkazy.</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} className="mb-4">
                    <Card className="h-100">
                        <Card.Header>
                            <h5 className="mb-0">Mobilní optimalizace</h5>
                        </Card.Header>
                        <Card.Body className="d-flex flex-column justify-content-center">
                            {seoData.is_mobile_friendly !== undefined ? (
                                <div className="text-center py-4">
                                    {seoData.is_mobile_friendly ? (
                                        <>
                                            <i className="fas fa-mobile-alt text-success" style={{ fontSize: '2rem' }}></i>
                                            <p className="mt-2">Web je optimalizován pro mobilní zařízení.</p>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-exclamation-triangle text-danger" style={{ fontSize: '2rem' }}></i>
                                            <p className="mt-2">Web není optimalizován pro mobilní zařízení!</p>
                                            <Alert variant="danger">
                                                <i className="fas fa-info-circle me-2"></i>
                                                Mobilní optimalizace je důležitým faktorem pro SEO. Doporučujeme upravit design webu
                                                pro lepší zobrazení na mobilních zařízeních.
                                            </Alert>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <p>Informace o mobilní optimalizaci nejsou k dispozici.</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">SEO problémy k vyřešení</h5>
                            <Badge bg="warning" pill>{seoData.issues ? seoData.issues.length : 0}</Badge>
                        </Card.Header>
                        <Card.Body>
                            {seoData.issues && seoData.issues.length > 0 ? (
                                <Table responsive hover>
                                    <thead>
                                    <tr>
                                        <th style={{ width: '15%' }}>Závažnost</th>
                                        <th style={{ width: '25%' }}>Typ</th>
                                        <th style={{ width: '60%' }}>Problém</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {seoData.issues.map((issue, index) => (
                                        <tr key={index}>
                                            <td>{getSeverityBadge(issue.severity)}</td>
                                            <td>{issue.type.replace(/_/g, ' ')}</td>
                                            <td>{issue.message}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="fas fa-check-circle text-success" style={{ fontSize: '2rem' }}></i>
                                    <p className="mt-2">Nebyly nalezeny žádné SEO problémy. Váš web je v pořádku!</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>


            <div className="d-flex justify-content-end mb-4">
                <Button
                    variant="primary"
                    disabled={loading || scanInProgress}
                    onClick={() => handleRunSeoScan(website.id)}
                >
                    {loading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Kontroluji...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-sync-alt me-2"></i> Spustit novou kontrolu
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
};

export default WebsiteSEO;